/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode, useId } from 'react';
import {
  FieldError,
  FieldErrors,
  FieldValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { QuestionType } from '@/api/gql/graphql';
import quizzImg from '@/assets/icons/quizzImg.svg';
import {
  Button,
  Checkbox,
  FileUpload,
  SelectDropDown,
  TextField,
} from '@/components/Common';
import { TextfieldType } from '@/components/Common/TextField/TextField.types';

interface QuestionFormProps {
  QuizzQuestionType: { _id: QuestionType; name: QuestionType }[];
  register: UseFormRegister<FieldValues>;
  errors: FieldErrors<FieldValues>;
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  onSubmit: (values: any) => void;
  setValue: UseFormSetValue<FieldValues>;
  handleSelectedType: (type: QuestionType) => void;
  selectedType: QuestionType | null;
  response1Check: boolean;
  setResponse1Check: (value: boolean) => void;
  response2Check: boolean;
  setResponse2Check: (value: boolean) => void;
  response3Check: boolean;
  setResponse3Check: (value: boolean) => void;
  response4Check: boolean;
  setResponse4Check: (value: boolean) => void;
  loading?: boolean;
  initialSelectedOptions?: any;
  submitButtonText?: string;
  isEditable?: boolean;
  editButtons?: ReactNode;
}

const QuestionForm = ({
  QuizzQuestionType,
  register,
  errors,
  handleSubmit,
  onSubmit,
  setValue,
  handleSelectedType,
  selectedType,
  response1Check,
  setResponse1Check,
  response2Check,
  setResponse2Check,
  response3Check,
  setResponse3Check,
  response4Check,
  setResponse4Check,
  loading,
  initialSelectedOptions,
  submitButtonText = 'Ajouter',
  editButtons,
  isEditable = false,
}: QuestionFormProps) => {
  const id = useId();

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      <SelectDropDown
        disabled={false}
        options={QuizzQuestionType || []}
        handleChange={(selectedType: any) => {
          handleSelectedType(selectedType._id);
          setValue('type', selectedType._id, { shouldValidate: true });
        }}
        name="type"
        initialSelectedOptions={
          selectedType
            ? { _id: selectedType, name: selectedType }
            : initialSelectedOptions
        }
        placeholder="Type de question"
        helpText={(errors.type as FieldError)?.message}
        errorStyle="text-red-500"
        setValue={setValue}
      />
      <TextField
        textFieldType={TextfieldType.TEXTAREA}
        placeholder="Saisir la question ici"
        textAreaStyle={{ background: '#1D1D1D' }}
        className="text-white  border border-solid border-[#2F2E2E]"
        name="name"
        register={register('name')}
        helpText={(errors.name as FieldError)?.message}
        errorStyle="text-red-500"
      />
      <div className="flex gap-2 items-center justify-start bg-[#1D1D1D] rounded-md max-h-12 pl-2">
        <div className="flex justify-center items-center">
          <img src={quizzImg} className="max-h-full max-w-full " />
        </div>
        <div className="flex-grow">
          <FileUpload
            id={`${id}-picture`}
            name="picture"
            placeholder="image"
            allowedFileTypes={['image/jpeg', 'image/png']}
            setValue={setValue}
            handleChange={(file) => {
              setValue('picture', file, { shouldValidate: true });
            }}
          />
        </div>
      </div>

      {selectedType === QuestionType.TrueOrFalse ? (
        <>
          <div className="flex gap-3 items-center justify-start">
            <Checkbox
              checked={response1Check}
              onClick={() => {
                setResponse1Check(!response1Check);
                if (response2Check) setResponse2Check(false);
              }}
            />
            <TextField
              inputStyle={{ background: '#1D1D1D' }}
              textFieldType={TextfieldType.INPUT}
              placeholder="Vrai"
              className="text-white"
              name="reponse1"
              register={register('reponse1')}
              helpText={(errors.reponse1 as FieldError)?.message}
              errorStyle="text-red-500"
            />
          </div>
          <div className="flex gap-3 items-center justify-start">
            <Checkbox
              checked={response2Check}
              onClick={() => {
                setResponse2Check(!response2Check);
                if (response1Check) setResponse1Check(false);
              }}
            />
            <TextField
              inputStyle={{ background: '#1D1D1D' }}
              textFieldType={TextfieldType.INPUT}
              placeholder="Faux"
              className="text-white"
              name="reponse2"
              register={register('reponse2')}
              helpText={(errors.reponse2 as FieldError)?.message}
              errorStyle="text-red-500"
            />
          </div>
        </>
      ) : (
        <>
          <div className="flex gap-3 items-center justify-start">
            <Checkbox
              checked={response1Check}
              onClick={() => setResponse1Check(!response1Check)}
            />
            <TextField
              inputStyle={{ background: '#1D1D1D' }}
              textFieldType={TextfieldType.INPUT}
              placeholder="Reponse 1"
              className="text-white"
              name="reponse1"
              register={register('reponse1')}
              helpText={(errors.reponse1 as FieldError)?.message}
              errorStyle="text-red-500"
            />
          </div>
          <div className="flex gap-3 items-center justify-start">
            <Checkbox
              checked={response2Check}
              onClick={() => setResponse2Check(!response2Check)}
            />
            <TextField
              inputStyle={{ background: '#1D1D1D' }}
              textFieldType={TextfieldType.INPUT}
              placeholder="Reponse 2"
              className="text-white"
              name="reponse2"
              register={register('reponse2')}
              helpText={(errors.reponse2 as FieldError)?.message}
              errorStyle="text-red-500"
            />
          </div>
          <div className="flex gap-3 items-center justify-start">
            <Checkbox
              checked={response3Check}
              onClick={() => setResponse3Check(!response3Check)}
            />
            <TextField
              inputStyle={{ background: '#1D1D1D' }}
              textFieldType={TextfieldType.INPUT}
              placeholder="Reponse 3"
              className="text-white"
              name="reponse3"
              register={register('reponse3')}
              helpText={(errors.reponse3 as FieldError)?.message}
              errorStyle="text-red-500"
            />
          </div>
          <div className="flex gap-3 items-center justify-start">
            <Checkbox
              checked={response4Check}
              onClick={() => setResponse4Check(!response4Check)}
            />
            <TextField
              inputStyle={{ background: '#1D1D1D' }}
              textFieldType={TextfieldType.INPUT}
              placeholder="Reponse 4"
              className="text-white"
              name="reponse4"
              register={register('reponse4')}
              helpText={(errors.reponse4 as FieldError)?.message}
              errorStyle="text-red-500"
            />
          </div>
        </>
      )}

      <TextField
        textFieldType={TextfieldType.TEXTAREA}
        placeholder="Explication de la reponse"
        className="text-white  border border-solid border-[#2F2E2E]"
        textAreaStyle={{ background: '#1D1D1D' }}
        name="description"
        register={register('description')}
        helpText={(errors.description as FieldError)?.message}
        errorStyle="text-red-500"
      />
      {isEditable && editButtons}

      <Button
        type="submit"
        title={`${loading ? 'Ajout en cours...' : submitButtonText}`}
        className={`${isEditable ? 'hidden' : 'block'} `}
      />
    </form>
  );
};

export default QuestionForm;
